import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import {
  Event as EventIcon,
  History as HistoryIcon,
  Cancel as CancelIcon,
  CalendarToday,
  Pending as PendingIcon,
} from '@mui/icons-material';
import { useApp } from '../context/AppContext';
import { appointmentService } from '../services/appointmentService';
import AppointmentCard from '../components/AppointmentCard';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const AppointmentsDashboardPage: React.FC = () => {
  const { state } = useApp();
  const user = state.user;
  const [activeTab, setActiveTab] = useState(0);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await appointmentService.getMyAppointments();
      setAppointments(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleAppointmentAction = (accepted?: boolean) => {
    // Refresh appointments after any action
    fetchAppointments();
    // If accepting an appointment, switch to Upcoming tab to show the accepted appointment
    if (accepted === true && user?.role === 'CAREGIVER') {
      // Use setTimeout to ensure state updates happen after the refresh
      setTimeout(() => {
        setActiveTab(1); // Upcoming tab index for caregivers
      }, 100);
    }
  };

  // Filter appointments by status
  const now = new Date();
  const upcomingStatuses =
    user?.role === 'PATIENT'
      ? ['REQUESTED', 'SCHEDULED', 'CONFIRMED']
      : ['SCHEDULED', 'CONFIRMED'];
  const upcomingAppointments = appointments.filter(apt => {
    const scheduledDate = new Date(apt.scheduledAt);
    return scheduledDate >= now && upcomingStatuses.includes(apt.status);
  }).sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime());

  const pastAppointments = appointments.filter(apt => {
    const scheduledDate = new Date(apt.scheduledAt);
    return scheduledDate < now || apt.status === 'COMPLETED';
  }).sort((a, b) => new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime());

  const cancelledAppointments = appointments.filter(apt => 
    apt.status === 'CANCELLED' || apt.status === 'NO_SHOW'
  ).sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  // Filter pending appointments (REQUESTED status) - show all regardless of date
  const pendingAppointments = appointments.filter(apt => 
    apt.status === 'REQUESTED'
  ).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const getNextAppointment = () => {
    return upcomingAppointments[0] || null;
  };

  const nextAppointment = getNextAppointment();

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarToday />
            My Appointments
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {user?.role === 'PATIENT' 
              ? 'View and manage your scheduled appointments with caregivers'
              : 'View and manage your scheduled appointments with patients'}
          </Typography>
        </Box>

        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Summary Cards */}
        {appointments.length > 0 && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Total Appointments
                  </Typography>
                  <Typography variant="h4">{appointments.length}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Upcoming
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {upcomingAppointments.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Next Appointment
                  </Typography>
                  {nextAppointment ? (
                    <Typography variant="body2">
                      {new Date(nextAppointment.scheduledAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      None scheduled
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            {user?.role === 'CAREGIVER' && (
              <Tab 
                icon={<PendingIcon />} 
                iconPosition="start" 
                label={`Pending (${pendingAppointments.length})`} 
              />
            )}
            <Tab 
              icon={<EventIcon />} 
              iconPosition="start" 
              label={`Upcoming (${upcomingAppointments.length})`} 
            />
            <Tab 
              icon={<HistoryIcon />} 
              iconPosition="start" 
              label={`Past (${pastAppointments.length})`} 
            />
            <Tab 
              icon={<CancelIcon />} 
              iconPosition="start" 
              label={`Cancelled (${cancelledAppointments.length})`} 
            />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        {user?.role === 'CAREGIVER' && (
          <TabPanel value={activeTab} index={0}>
            {pendingAppointments.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <PendingIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No Pending Appointments
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Appointment requests from patients will appear here for your review.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {pendingAppointments.map((appointment) => (
                  <Grid item xs={12} key={appointment.id}>
                    <AppointmentCard 
                      appointment={appointment} 
                      userRole={user?.role}
                      onAction={(accepted) => handleAppointmentAction(accepted)}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </TabPanel>
        )}

        <TabPanel value={activeTab} index={user?.role === 'CAREGIVER' ? 1 : 0}>
          {upcomingAppointments.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <EventIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Upcoming Appointments
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {user?.role === 'PATIENT' 
                  ? "You don't have any scheduled appointments. Start by searching for caregivers."
                  : "You don't have any upcoming appointments with patients."}
              </Typography>
              {user?.role === 'PATIENT' && (
                <Button variant="contained" href="/search">
                  Find Caregivers
                </Button>
              )}
            </Box>
          ) : (
            <Grid container spacing={3}>
              {upcomingAppointments.map((appointment) => (
                <Grid item xs={12} key={appointment.id}>
                  <AppointmentCard 
                    appointment={appointment} 
                    userRole={user?.role}
                    onAction={handleAppointmentAction}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={user?.role === 'CAREGIVER' ? 2 : 1}>
          {pastAppointments.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <HistoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Past Appointments
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your completed appointments will appear here.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {pastAppointments.map((appointment) => (
                <Grid item xs={12} key={appointment.id}>
                  <AppointmentCard 
                    appointment={appointment} 
                    userRole={user?.role}
                    onAction={handleAppointmentAction}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={user?.role === 'CAREGIVER' ? 3 : 2}>
          {cancelledAppointments.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CancelIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Cancelled Appointments
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cancelled appointments will appear here.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {cancelledAppointments.map((appointment) => (
                <Grid item xs={12} key={appointment.id}>
                  <AppointmentCard 
                    appointment={appointment} 
                    userRole={user?.role}
                    onAction={handleAppointmentAction}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>
      </Box>
    </Container>
  );
};

export default AppointmentsDashboardPage;


